import { useEffect, useRef, useState } from 'react';
import { useSession } from '../contexts/SessionContext';
import { useCalendar } from '../contexts/CalendarContext';
import { getPossessionFields, fieldStateKey } from '../utils/dataFields';
import { cancelBooking, getSchedule } from '../utils/api';
import { deleteCookie } from '../utils/cookies';
import { downloadICal } from '../utils/ical';
import './SuccessScreen.css';

const SuccessScreen = () => {
  const { setCancelled, sessionId, schedule, setSchedule, setCurrentStep, personalInfo } = useSession();
  const { calendarId, fields } = useCalendar();
  const possessionLabel = fields?.possession_label || 'Details';
  const possessionSummary = getPossessionFields(fields)
    .map((f) => personalInfo?.[fieldStateKey(f)])
    .filter(Boolean)
    .join(' ');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // CIN-6621: the API tells us whether a change made here can actually reach the
  // shop's system. Strict === false so an older payload without change_sync (or a
  // schedule that has not hydrated yet) keeps today's behaviour.
  const changeSync = schedule?.change_sync;
  const cancelBlocked = changeSync?.cancel_syncs === false;
  const editBlocked = changeSync?.reschedule_syncs === false;
  // Assumption: cancel and reschedule sync together for every current write-back
  // integration (both flags flip as a pair), so one combined "call the shop"
  // sentence covers both. If a vendor ever supports one but not the other, split
  // this into per-action copy instead of widening the single notice.
  const changeBlocked = cancelBlocked || editBlocked;
  const locationPhone = schedule?.location_phone;
  const NOTICE_ID = 'change-blocked-notice';

  // CIN-6621: on the just-booked path Scheduler.js hand-builds `schedule` from
  // the POST /book response, so it carries no change_sync/location_phone and
  // nothing else re-fetches in that tab (the session cookie is already gone).
  // Hydrate once from GET /calendar/sessions/{id}/schedule so the guard can
  // latch. One shot only: the ref is set before the request, so a payload that
  // still has no change_sync (older API) cannot cause a refetch loop, and a
  // failed request leaves the guard off, i.e. exactly today's behaviour — the
  // backend's own rejection remains the backstop.
  const hydrationAttempted = useRef(false);
  useEffect(() => {
    if (hydrationAttempted.current) return;
    if (!sessionId || !schedule) return;
    if (schedule.change_sync !== undefined) {
      hydrationAttempted.current = true;
      return;
    }
    hydrationAttempted.current = true;

    let discarded = false;
    let settled = false;
    Promise.resolve(getSchedule(sessionId))
      .then((data) => {
        settled = true;
        if (discarded || !data || typeof setSchedule !== 'function') return;
        // Merge so locally-derived display fields (possession summary, service
        // names) survive if the API payload does not repeat them.
        setSchedule((previous) => ({ ...previous, ...data }));
      })
      .catch(() => {
        settled = true;
        // Silent: no change_sync means the widget behaves as it always has.
      });

    return () => {
      discarded = true;
      // StrictMode double-invokes effects in development: the first setup fires
      // the request and the immediate cleanup discards it, so the ref has to be
      // re-armed for the second setup to fetch again — otherwise the guard never
      // latches under `npm start`. A request that already settled leaves the ref
      // set, which is what stops a payload lacking change_sync from refetching.
      if (!settled) hydrationAttempted.current = false;
    };
  }, [sessionId, schedule, setSchedule]);

  // Format the date and time for display
  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-US', options);
  };

  const onNewBooking = () => {
    // Clear session and reload
    const cookieName = `scheduler_session_${calendarId}`;
    deleteCookie(cookieName);

    const url = new URL(window.location.href);
    url.searchParams.delete('sessionId');
    window.location.href = url.toString();
  }
  const onEditBooking = () => {
    // CIN-6621: editing re-books the appointment, which cannot reach the shop's
    // system, so a keyboard/programmatic trigger must not jump back to step 1.
    if (editBlocked) return;
    // Go back to step 1
    setCurrentStep(1);
  }
  const onCancelBooking = () => {
    if (cancelBlocked) return;
    setShowCancelModal(true);
  }

  const onDownloadCalendar = () => {
    if (schedule) {
      downloadICal(schedule);
    }
  }

  const confirmCancelBooking = async () => {
    setIsCancelling(true);
    setCancelError(null);

    try {
      await cancelBooking(sessionId);
      
      // Notify parent window of booking cancellation
      window.parent.postMessage({
        type: 'BOOKING_CANCELLED',
        data: {
          booking_id: schedule?.id,
          schedule_id: sessionId,
          cancelled_at: new Date().toISOString(),
          appointment_date: schedule?.appointment_naive,
          customer: {
            first_name: schedule?.customer_first_name,
            last_name: schedule?.customer_last_name
          },
          services: schedule?.type,
          location: schedule?.location_name
        }
      }, '*');
      
      setCancelled(true);
      setShowCancelModal(false);
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      setCancelError(error.response?.data?.message || 'Failed to cancel booking. Please try again.');
      setIsCancelling(false);
    }
  }

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancelError(null);
  }

  return (
    <div className="step-content success-screen">
      <div className="success-icon">✓</div>
      <h2>Thank You for Booking!</h2>
      <p className="subtitle">
        Thank you {schedule?.customer_first_name}, your appointment has been successfully booked.
      </p>

      <div className="appointment-details">
        <h3>Appointment Details</h3>
        <div className="detail-row">
          <span className="detail-label">Date & Time:</span>
          <span className="detail-value">{formatDateTime(schedule?.appointment_naive)}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Location:</span>
          <span className="detail-value">{schedule?.location_name}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Services:</span>
          <span className="detail-value">
            {schedule?.type}
          </span>
        </div>
        {possessionSummary && (
          <div className="detail-row">
            <span className="detail-label">{possessionLabel}:</span>
            <span className="detail-value">{possessionSummary}</span>
          </div>
        )}
        <button
          className="download-calendar-button"
          onClick={onDownloadCalendar}
          title="Download calendar event"
        >
          📅 Add to Calendar
        </button>
      </div>

      <p className="body-text">
        We'll send a confirmation to your phone number shortly.
      </p>

      {changeBlocked && (
        <div
          className="change-blocked-notice"
          id={NOTICE_ID}
          role="status"
          aria-live="polite"
          data-testid="change-blocked-notice"
        >
          To change or cancel this appointment, please call the shop directly.
          {locationPhone && (
            <>
              {' '}
              <a
                className="change-blocked-phone"
                data-testid="change-blocked-phone"
                href={`tel:${String(locationPhone).replace(/[^\d+]/g, '')}`}
              >
                {locationPhone}
              </a>
            </>
          )}
        </div>
      )}

      <div className="action-buttons">
        <button
          className={`action-button secondary${cancelBlocked ? ' disabled' : ''}`}
          onClick={onCancelBooking}
          disabled={cancelBlocked}
          aria-describedby={cancelBlocked ? NOTICE_ID : undefined}
          data-testid="cancel-booking-button"
        >
          Cancel Booking
        </button>
        <button
          className={`action-button secondary${editBlocked ? ' disabled' : ''}`}
          onClick={onEditBooking}
          disabled={editBlocked}
          aria-describedby={editBlocked ? NOTICE_ID : undefined}
          data-testid="edit-booking-button"
        >
          Edit Booking
        </button>
        <button
          className="action-button primary"
          onClick={onNewBooking}
        >
          Start New Booking
        </button>
      </div>

      {showCancelModal && (
        <div className="cancel-modal-overlay" onClick={closeCancelModal}>
          <div className="cancel-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Booking?</h3>
            <p>Are you sure you want to cancel this booking?</p>
            <p className="cancel-modal-warning-text">
              <strong>Warning:</strong> This action cannot be undone.
            </p>
            {cancelError && (
              <p className="cancel-modal-error-text" style={{ color: '#dc3545', marginTop: '10px' }}>
                {cancelError}
              </p>
            )}
            <div className="cancel-modal-actions">
              <button
                className="cancel-modal-button cancel"
                onClick={closeCancelModal}
                disabled={isCancelling}
              >
                Keep Booking
              </button>
              <button
                className="cancel-modal-button confirm"
                onClick={confirmCancelBooking}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel Booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuccessScreen;